import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { KeyValue, Environment, RepoRef } from '../types/environment';
import type { GoogleUser } from '../types/auth';
import RepoSelector from '../components/RepoSelector';
import Cookies from 'js-cookie';

interface OrgOption {
	org: string;
}

export default function EditEnvironmentPage() {
    const { environmentID } = useParams<{ environmentID: string }>();
    const effectiveEnvironmentID = environmentID ?? 'default';
    const [orgs, setOrgs] = useState<OrgOption[]>([]);
    const [selectedRepos, setSelectedRepos] = useState<RepoRef[]>([]);
    const [context, setContext] = useState('');
    const [image, setImage] = useState('');
    const [envVars, setEnvVars] = useState<KeyValue[]>([]);
    const [secrets, setSecrets] = useState<KeyValue[]>([]);
    const [bootstrap, setBootstrap] = useState('');
    const [endpoints, setEndpoints] = useState<string[]>([]);
    const [errors, setErrors] = useState<string[]>([]);
    const [version, setVersion] = useState(0);
    const [currentSecretIndex, setCurrentSecretIndex] = useState<null | number>(null);
    const navigate = useNavigate();
    const CONTEXT_LIMIT = 25000;
    const BOOTSTRAP_LIMIT = 5000;
    const fieldStyle = { width: '100%', padding: '6px' } as const;
    const labelStyle = { width: '100%', display: 'flex', flexDirection: 'column' } as const;

	useEffect(() => {
		async function loadOrgs() {
            try {
                const resp = await fetch(`/api/github/orgs?environmentID=${encodeURIComponent(effectiveEnvironmentID)}`);
                if (resp.ok) {
                    const data: OrgOption[] = await resp.json();
                    setOrgs(data);
                } else {
                    setOrgs([]);
                }
            } catch (err) {
                console.error('load orgs error', err);
            }
        }
		loadOrgs();
	}, [effectiveEnvironmentID]);

    const [name, setName] = useState('');
    useEffect(() => {
        async function loadEnv() {
            try {
                const resp = await fetch(`/api/environments/${environmentID}`);
                if (resp.ok) {
                    const data: Environment = await resp.json();
				const repos = (data.Repos || []).map((r) => ({
					Org: r.Org,
					Repo: r.Repo.startsWith(`${r.Org}/`) ? r.Repo.slice(r.Org.length + 1) : r.Repo,
				}));
                    setSelectedRepos(repos);
                    setContext(data.Context || '');
                    setImage(data.Image || '');
                    setEnvVars(data.EnvVars || []);
                    setSecrets(data.Secrets || []);
                    setBootstrap(data.BootstrapScript || '');
                    setEndpoints(data.AllowedEndpoints || []);
                    setName(data.Name);
                    setVersion(data.Version);
                    hasSetDefaultName.current = !!data.Name;
                } else {
                    const msg = await resp.text();
                    setErrors((e) => [...e, msg]);
                }
            } catch (err) {
                console.error('GetEnvironment error', err);
                setErrors((e) => [...e, 'GetEnvironment error']);
            }
        }
        loadEnv();
    }, [environmentID]);

    const hasSetDefaultName = useRef(false);
    useEffect(() => {
        if (!hasSetDefaultName.current && !name && selectedRepos.length > 0) {
            const first = selectedRepos[0];
            setName(`${first.Org}/${first.Repo}`);
            hasSetDefaultName.current = true;
        }
    }, [selectedRepos, name]);

    const addEnvVar = () => setEnvVars([...envVars, { Key: '', Value: '' }]);
    const updateEnvVar = (i: number, field: 'Key' | 'Value', value: string) => {
        const copy = [...envVars];
        copy[i][field] = value;
        setEnvVars(copy);
    };
    const removeEnvVar = (i: number) => setEnvVars(envVars.filter((_, idx) => idx !== i));

    const addSecret = () => setSecrets([...secrets, { Key: '', Value: '' }]);
    const updateSecret = (i: number, field: 'Key' | 'Value', value: string) => {
        const copy = [...secrets];
        copy[i][field] = value;
        setSecrets(copy);
    };
    const removeSecret = (i: number) => setSecrets(secrets.filter((_, idx) => idx !== i));

    const addEndpoint = () => setEndpoints([...endpoints, '']);
    const updateEndpoint = (i: number, value: string) => {
        const copy = [...endpoints];
        copy[i] = value;
        setEndpoints(copy);
    };
    const removeEndpoint = (i: number) => setEndpoints(endpoints.filter((_, idx) => idx !== i));

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        try {
            const storedUser = Cookies.get('User');
            const email = storedUser ? (JSON.parse(storedUser) as GoogleUser).email : '';
			const repoPairs = selectedRepos.map((r) => ({ Org: r.Org, Repo: r.Repo }));
            const resp = await fetch(`/api/environments/${environmentID}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'X-User-Email': email },
                body: JSON.stringify({
                    Repos: repoPairs,
                    Name: name,
                    Context: context,
                    Image: image,
                    EnvVars: envVars,
                    Secrets: secrets,
                    BootstrapScript: bootstrap,
                    AllowedEndpoints: endpoints,
                    Version: version,
                }),
            });
            if (resp.ok) {
                navigate('/environments');
            } else {
                const msg = await resp.text();
                setErrors((errs) => [...errs, msg]);
            }
        } catch (err) {
            console.error('UpdateEnvironment error', err);
            setErrors((errs) => [...errs, 'UpdateEnvironment error']);
        }
    }

    return (
        <div style={{ paddingTop: '60px', maxWidth: '800px', margin: '0 auto' }}>
            {errors.map((er, i) => (
                <div key={i} style={{ color: 'red', marginBottom: '8px' }}>
                    {er}
                </div>
            ))}
            <h1>Edit Environment</h1>
            <form onSubmit={handleSubmit}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <RepoSelector environmentID={effectiveEnvironmentID} orgs={orgs} selectedRepos={selectedRepos} setSelectedRepos={setSelectedRepos} />
                    <label style={labelStyle}>
                        Name
                        <input value={name} onChange={(e) => setName(e.target.value)} style={fieldStyle} />
                    </label>
                    <label style={labelStyle}>
                        Context (optional AI instructions about this environment)
                        <textarea
                            value={context}
                            onChange={(e) => setContext(e.target.value)}
                            style={{ ...fieldStyle, minHeight: '80px' }}
                            maxLength={CONTEXT_LIMIT}
                        />
                        <div style={{ textAlign: 'right', fontSize: '0.8em', color: '#555' }}>
                            {context.length}/{CONTEXT_LIMIT}
                        </div>
                    </label>
                    <label style={labelStyle}>
                        Image
                        <input value={image} onChange={(e) => setImage(e.target.value)} style={fieldStyle} />
                    </label>
                    <div>
                        <div>Environment variables</div>
                        <button
                            type="button"
                            className="btn-small btn-secondary"
                            onClick={addEnvVar}
                            style={{ marginTop: '4px' }}
                        >
                            Add
                        </button>
                        {envVars.map((v, i) => (
                            <div key={i} style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
                                <input
                                    value={v.Key}
                                    onChange={(e) => updateEnvVar(i, 'Key', e.target.value)}
                                    placeholder="Key"
                                    style={{ flex: 1, padding: '6px' }}
                                />
                                <input
                                    value={v.Value}
                                    onChange={(e) => updateEnvVar(i, 'Value', e.target.value)}
                                    placeholder="Value"
                                    style={{ flex: 1, padding: '6px' }}
                                />
                                <button
                                    type="button"
                                    className="btn-small btn-secondary"
                                    onClick={() => removeEnvVar(i)}
                                >
                                    Delete
                                </button>
                            </div>
                        ))}
                    </div>
                    <div>
                        <div>Secrets</div>
                        <button
                            type="button"
                            className="btn-small btn-secondary"
                            onClick={addSecret}
                            style={{ marginTop: '4px' }}
                        >
                            Add
                        </button>
                        {secrets.map((s, i) => (
                            <div key={i} style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
                                <input
                                    value={s.Key}
                                    onChange={(e) => updateSecret(i, 'Key', e.target.value)}
                                    placeholder="Key"
                                    style={{ flex: 1, padding: '6px' }}
                                />
                                <input
                                    type="password"
                                    value={currentSecretIndex == i ? s.Value : '*'.repeat(s.Value.length)}
                                    onChange={(e) => {
                                        setCurrentSecretIndex(i);
                                        updateSecret(i, 'Value', e.target.value);
                                    }}
                                    onBlur={(_) => setCurrentSecretIndex(null)}
                                    placeholder="Value"
                                    style={{ flex: 1, padding: '6px' }}
                                />
                                <button
                                    type="button"
                                    className="btn-small btn-secondary"
                                    onClick={() => removeSecret(i)}
                                >
                                    Delete
                                </button>
                            </div>
                        ))}
                    </div>
                    <label style={labelStyle}>
                        Bootstrap script
                        <textarea
                            value={bootstrap}
                            onChange={(e) => setBootstrap(e.target.value)}
                            style={{ ...fieldStyle, minHeight: '80px' }}
                            maxLength={BOOTSTRAP_LIMIT}
                        />
                        <div style={{ textAlign: 'right', fontSize: '0.8em', color: '#555' }}>
                            {bootstrap.length}/{BOOTSTRAP_LIMIT}
                        </div>
                    </label>
                    <div>
                        <div>Allowed endpoints</div>
                        <button
                            type="button"
                            className="btn-small btn-secondary"
                            onClick={addEndpoint}
                            style={{ marginTop: '4px' }}
                        >
                            Add
                        </button>
                        {endpoints.map((ep, i) => (
                            <div key={i} style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
                                <input
                                    value={ep}
                                    onChange={(e) => updateEndpoint(i, e.target.value)}
                                    placeholder="Endpoint"
                                    style={{ flex: 1, padding: '6px' }}
                                />
                                <button
                                    type="button"
                                    className="btn-small btn-secondary"
                                    onClick={() => removeEndpoint(i)}
                                >
                                    Delete
                                </button>
                            </div>
                        ))}
                    </div>
                    <div style={{ textAlign: 'center', marginTop: '16px' }}>
                        <button type="submit" style={{ padding: '6px 12px' }}>
                            Update Environment
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
}
